/**
 * InvoiceQuickModal - Unified invoice creation modal
 *
 * This modal can be used from:
 * 1. MorningPage - standalone invoice creation (no pre-selection)
 * 2. PendingAppointmentsCarousel - quick invoice with pre-selected patient/appointment
 *
 * Features:
 * - Patient picker (dropdown)
 * - Patient's last 10 appointments with invoice status
 * - Load more appointments (10 at a time)
 * - Click to select MULTIPLE appointments to link
 *
 * Document type / payment defaults come from clinic.morningIntegration
 * (settings editor), options are filtered to what the business may issue.
 *
 * Error Codes: CK-8812, CK-8813, CK-8817
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { trackFeature, FEATURE_KEYS } from "../../services/telemetry";
import { useSelector } from "react-redux";
import { httpsCallable } from "firebase/functions";
import { functions, getAppointments } from "../../firebase";
import {
  selectClinicId,
  selectClinicProfile,
  selectTherapistId,
} from "../../store/slices/authSlice";
import { useGetPatientsQuery } from "../../store/services/patientsApi";
import {
  Receipt,
  X,
  CheckCircle,
  WarningCircle,
  User,
  CurrencyCircleDollar,
  FileText,
  Eye,
  CalendarBlank,
  CaretDown,
  Check,
  LinkSimple,
  ArrowsClockwise,
} from "@phosphor-icons/react";
import {
  formatCurrency,
  MORNING_DOCUMENT_HINTS,
  MORNING_PAYMENT_TYPES,
  getAllowedDocumentTypeOptions,
  getPaymentTypeOptions,
  getDocumentNeeds,
  getMorningAppType,
  isMorningExempt,
  mapPaymentMethodToMorning,
  resolveClinicDefaultDocumentType,
} from "../../services/morningService";
import { HelpButton } from "../shared";
import ModalCloseButton from "../ui/ModalCloseButton";
import { sortPatientsByName } from "../../utils/patientSortUtils";
import "./InvoiceQuickModal.css";
import ContentLoader from "../shared/ContentLoader";
import LoaderLoop from "../shared/loader/LoaderLoop";

// Number of appointments to load per page
const APPOINTMENTS_PAGE_SIZE = 10;

function InvoiceQuickModal({
  isOpen,
  onClose,
  // Pre-filled props (from carousel)
  appointment: prefilledAppointment = null,
  // Transaction prop (from Kanban - financial transaction)
  transaction: prefilledTransaction = null,
  patientName: prefilledPatientName = "",
  amount: prefilledAmount = "",
  paymentMethod: prefilledPaymentMethod = null,
  onSuccess,
}) {
  const clinicId = useSelector(selectClinicId);
  const clinicProfile = useSelector(selectClinicProfile);
  const therapistId = useSelector(selectTherapistId);

  // Clinic defaults (settings editor / wizard). Legacy settings are remapped
  // client-side for the initial value; the server re-resolves on create.
  const morningSettings = clinicProfile?.morningIntegration || null;
  const defaultDocType = useMemo(
    () => resolveClinicDefaultDocumentType(morningSettings),
    [morningSettings]
  );
  const defaultPayType =
    Number(morningSettings?.defaultPaymentType) || MORNING_PAYMENT_TYPES.CREDIT_CARD;
  const documentTypeOptions = useMemo(
    () => getAllowedDocumentTypeOptions(morningSettings?.allowedDocumentTypes, isMorningExempt(morningSettings)),
    [morningSettings]
  );
  const resolvePaymentType = useCallback(
    () =>
      prefilledPaymentMethod
        ? mapPaymentMethodToMorning(prefilledPaymentMethod)
        : defaultPayType,
    [prefilledPaymentMethod, defaultPayType]
  );

  // Form state
  const [isCreating, setIsCreating] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [documentType, setDocumentType] = useState(defaultDocType);
  const [paymentType, setPaymentType] = useState(resolvePaymentType);
  const [description, setDescription] = useState("טיפול");
  const [amount, setAmount] = useState(prefilledAmount || "");
  const [patientName, setPatientName] = useState(prefilledPatientName || "");

  // Patient selection state
  const [selectedPatientId, setSelectedPatientId] = useState(
    prefilledAppointment?.patientId || prefilledTransaction?.patientId || null
  );
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const [patientSearchQuery, setPatientSearchQuery] = useState("");

  // Appointment selection state - ARRAY for multiple selection
  const [selectedAppointments, setSelectedAppointments] = useState(
    prefilledAppointment ? [prefilledAppointment] : []
  );
  const [patientAppointments, setPatientAppointments] = useState([]);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  const [appointmentsPage, setAppointmentsPage] = useState(1);
  const [hasMoreAppointments, setHasMoreAppointments] = useState(false);

  // Get patients list
  const { data: patientsData } = useGetPatientsQuery(
    { clinicId, therapistId, pageSize: 200 },
    { skip: !clinicId || !therapistId || !isOpen }
  );

  const patients = patientsData?.patients || [];

  // Filter patients for dropdown
  const filteredPatients = sortPatientsByName(patients).filter((p) =>
    `${p.firstName} ${p.lastName}`
      .toLowerCase()
      .includes(patientSearchQuery.toLowerCase())
  );

  // Check if we're in pre-filled mode (from carousel or kanban)
  const isPrefilledMode = !!prefilledAppointment || !!prefilledTransaction;

  // Calculate total amount from selected appointments
  const calculatedTotalFromAppointments = useMemo(() => {
    if (selectedAppointments.length === 0) return 0;
    return selectedAppointments.reduce((sum, apt) => {
      const aptAmount = apt.actualPaidAmount || apt.paymentAmount || 0;
      return sum + Number(aptAmount);
    }, 0);
  }, [selectedAppointments]);

  // Load patient appointments
  const loadPatientAppointments = useCallback(
    async (patientId, page = 1, append = false) => {
      if (!clinicId || !patientId) return;

      setAppointmentsLoading(true);

      try {
        // Get appointments from the last 6 months
        const endDate = new Date();
        const startDate = new Date();
        startDate.setMonth(startDate.getMonth() - 6);

        const allAppointments = await getAppointments(
          clinicId,
          startDate,
          endDate,
          therapistId
        );

        // Filter for this patient and completed appointments
        const patientAppts = allAppointments
          .filter(
            (apt) => apt.patientId === patientId && apt.status === "completed"
          )
          .sort((a, b) => new Date(b.startTime) - new Date(a.startTime));

        // Paginate
        const endIndex = page * APPOINTMENTS_PAGE_SIZE;
        const paginatedAppts = patientAppts.slice(0, endIndex);

        if (append) {
          setPatientAppointments(paginatedAppts);
        } else {
          setPatientAppointments(paginatedAppts);
        }

        setHasMoreAppointments(patientAppts.length > endIndex);
        setAppointmentsPage(page);
      } catch (err) {
        console.error("[CK-8813] Error loading patient appointments:", {
          errorCode: "CK-8813",
          errorMessage: err.message,
          patientId,
          timestamp: new Date().toISOString(),
        });
      } finally {
        setAppointmentsLoading(false);
      }
    },
    [clinicId, therapistId]
  );

  // Load appointments when patient is selected
  useEffect(() => {
    if (selectedPatientId && isOpen) {
      loadPatientAppointments(selectedPatientId, 1, false);
    } else {
      setPatientAppointments([]);
      setAppointmentsPage(1);
      setHasMoreAppointments(false);
    }
  }, [selectedPatientId, isOpen, loadPatientAppointments]);

  // Initialize with pre-filled values when modal opens
  useEffect(() => {
    if (isOpen && prefilledAppointment) {
      setSelectedPatientId(prefilledAppointment.patientId);
      setSelectedAppointments([prefilledAppointment]);
      setPatientName(prefilledPatientName || prefilledAppointment.patientName);
      setAmount(prefilledAmount?.toString() || "");
      setPaymentType(resolvePaymentType());
      setDocumentType(defaultDocType);
    } else if (isOpen && prefilledTransaction) {
      // Handle transaction from Kanban
      setSelectedPatientId(prefilledTransaction.patientId || null);
      setSelectedAppointments([]); // Transaction is not an appointment
      setPatientName(prefilledPatientName || prefilledTransaction.patientName || "");
      setAmount(prefilledAmount?.toString() || "");
      setPaymentType(resolvePaymentType());
      setDocumentType(defaultDocType);
    }
  }, [
    isOpen,
    prefilledAppointment,
    prefilledTransaction,
    prefilledPatientName,
    prefilledAmount,
    prefilledPaymentMethod,
    resolvePaymentType,
    defaultDocType,
  ]);

  // Keep the selected type inside the allowed list (settings may change while open)
  useEffect(() => {
    if (!documentTypeOptions.some((o) => o.value === documentType)) {
      setDocumentType(documentTypeOptions[0]?.value ?? defaultDocType);
    }
  }, [documentTypeOptions, documentType, defaultDocType]);

  const documentNeeds = getDocumentNeeds(documentType, morningSettings);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSuccess(null);
      setError(null);
      if (!isPrefilledMode) {
        setSelectedPatientId(null);
        setSelectedAppointments([]);
        setPatientName("");
        setAmount("");
        setDescription("טיפול");
        setDocumentType(defaultDocType);
        setPaymentType(defaultPayType);
        setPatientAppointments([]);
      }
    }
  }, [isOpen, isPrefilledMode, defaultDocType, defaultPayType]);

  // Handle patient selection
  const handleSelectPatient = (patient) => {
    setSelectedPatientId(patient.id);
    setPatientName(`${patient.firstName} ${patient.lastName}`);
    setSelectedAppointments([]); // Clear selected appointments
    setAmount(""); // Clear amount
    setShowPatientDropdown(false);
    setPatientSearchQuery("");
  };

  // Handle appointment selection - TOGGLE in array
  const handleSelectAppointment = (apt) => {
    const isSelected = selectedAppointments.some((a) => a.id === apt.id);

    if (isSelected) {
      // Remove from selection
      const newSelection = selectedAppointments.filter((a) => a.id !== apt.id);
      setSelectedAppointments(newSelection);

      // Update amount based on remaining selection
      if (newSelection.length > 0) {
        const newTotal = newSelection.reduce((sum, a) => {
          return sum + Number(a.actualPaidAmount || a.paymentAmount || 0);
        }, 0);
        setAmount(newTotal.toString());
      } else {
        setAmount("");
      }
    } else {
      // Add to selection
      const newSelection = [...selectedAppointments, apt];
      setSelectedAppointments(newSelection);

      // Update amount - sum of all selected appointments
      const newTotal = newSelection.reduce((sum, a) => {
        return sum + Number(a.actualPaidAmount || a.paymentAmount || 0);
      }, 0);
      setAmount(newTotal.toString());
    }
  };

  // Check if appointment is selected
  const isAppointmentSelected = (apt) => {
    return selectedAppointments.some((a) => a.id === apt.id);
  };

  // Load more appointments
  const handleLoadMoreAppointments = () => {
    if (!selectedPatientId || appointmentsLoading || !hasMoreAppointments)
      return;
    loadPatientAppointments(selectedPatientId, appointmentsPage + 1, true);
  };

  // Format date for display
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("he-IL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString("he-IL", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Check if appointment has invoice
  const hasInvoice = (apt) => {
    return (
      apt.isProducedReceipitForThisMeeting === true ||
      (apt.RecepitsDocsRefArray && apt.RecepitsDocsRefArray.length > 0)
    );
  };

  // Receipt date = the real payment date when we know it (YYYY-MM-DD, Israel
  // calendar day); the server falls back to today otherwise.
  const resolvePaymentDate = () => {
    const raw =
      prefilledTransaction?.actualPaymentDate ||
      prefilledTransaction?.paidAt ||
      prefilledAppointment?.paymentDate ||
      null;
    if (!raw) return undefined;
    try {
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return undefined;
      return d.toLocaleDateString("en-CA", { timeZone: "Asia/Jerusalem" });
    } catch (err) {
      console.error("[CK-8817] Could not resolve Morning payment date:", {
        errorCode: "CK-8817",
        errorMessage: err.message,
        timestamp: new Date().toISOString(),
      });
      return undefined;
    }
  };

  // Create invoice using Cloud Function
  const handleCreateInvoice = async () => {
    // Validate
    if (!patientName.trim()) {
      setError("יש להזין שם לקוח");
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      setError("יש להזין סכום תקין");
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      const createMorningDocument = httpsCallable(
        functions,
        "createMorningDocument"
      );

      // Get array of appointment IDs
      // Kanban/profile path: the transaction is not an "appointment" in the
      // picker, but it IS linked to one — pass that id so the Cloud Function
      // stamps RecepitsDocsRefArray / isProducedReceipitForThisMeeting on the
      // appointment and the catch-up carousel stops asking to invoice it again.
      const appointmentIds =
        selectedAppointments.length > 0
          ? selectedAppointments.map((apt) => apt.id)
          : prefilledTransaction?.appointmentId
            ? [prefilledTransaction.appointmentId]
            : [];

      const result = await createMorningDocument({
        clinicId,
        documentData: {
          type: documentType,
          amount: Number(amount),
          description: description,
          paymentType: documentNeeds.payment ? paymentType : undefined,
          appType:
            documentNeeds.payment && paymentType === MORNING_PAYMENT_TYPES.BIT
              ? getMorningAppType(prefilledPaymentMethod)
              : undefined,
          paymentDate: resolvePaymentDate(),
          client: {
            name: patientName,
          },
        },
        // Pass array of appointment IDs
        appointmentIds: appointmentIds.length > 0 ? appointmentIds : null,
        // Keep single appointmentId for backwards compatibility
        appointmentId: appointmentIds.length === 1 ? appointmentIds[0] : null,
        patientId: selectedPatientId || null,
      });

      if (result.data.success) {
        setSuccess(result.data);
        trackFeature(FEATURE_KEYS.MORNING_INVOICE);

        // Call onSuccess callback with the document data
        if (onSuccess) {
          onSuccess({
            documentId: result.data.document?.id,
            invoiceNumber: result.data.invoiceNumber,
            invoiceUrl: result.data.invoiceUrl,
            invoiceProvider: "morning",
            // Pass array of appointments that were linked
            appointmentIds: appointmentIds,
            appointments: selectedAppointments,
            // Keep single appointmentId for backwards compatibility
            appointmentId: appointmentIds.length === 1 ? appointmentIds[0] : null,
            patientId: selectedPatientId,
          });
        }

        // Auto close after 3 seconds
        setTimeout(() => {
          onClose();
        }, 3000);
      } else {
        console.error("[CK-8812] Error creating invoice:", {
          errorCode: "CK-8812",
          error: result.data.error,
          morningError: result.data.morningError,
          morningCode: result.data.morningCode,
          suggestedType: result.data.suggestedType,
          timestamp: new Date().toISOString(),
        });
        // Morning refused this document type for the business (2403): switch the
        // select to the suggested type so a retry is one click away.
        if (result.data.suggestedType) {
          setDocumentType(Number(result.data.suggestedType));
        }
        setError(result.data.error || result.data.morningError || "שגיאה ביצירת החשבונית");
      }
    } catch (err) {
      console.error("[CK-8812] Error creating invoice:", {
        errorCode: "CK-8812",
        errorMessage: err.message,
        timestamp: new Date().toISOString(),
      });
      setError("שגיאה ביצירת החשבונית. נסה שוב.");
    } finally {
      setIsCreating(false);
    }
  };

  // Skip invoice creation
  const handleSkip = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="invoice-quick-modal-overlay" onClick={handleSkip}>
      <div
        className="invoice-quick-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="invoice-quick-modal-header">
          <div className="header-icon">
            <Receipt size={24} weight="duotone" />
          </div>
          <div className="header-text">
            <h3>הפקת חשבונית</h3>
            <p>
              {isPrefilledMode
                ? "עבור תשלום שבוצע"
                : "בחר מטופל ופרטי החשבונית"}
            </p>
          </div>
          <div className="invoice-header-actions">
            <HelpButton featureKey="invoice_modal" />
            <ModalCloseButton onClick={handleSkip} />
          </div>
        </div>

        {/* Success State */}
        {success ? (
          <div className="invoice-quick-modal-body success-state">
            <CheckCircle size={56} weight="fill" className="success-icon" />
            <h4>החשבונית נוצרה בהצלחה!</h4>
            <p>
              מספר מסמך: <strong>{success.invoiceNumber}</strong>
            </p>
            {selectedAppointments.length > 0 && (
              <p className="linked-info">
                <LinkSimple size={16} />
                <span>
                  החשבונית שויכה ל-{selectedAppointments.length}{" "}
                  {selectedAppointments.length === 1 ? "פגישה" : "פגישות"}
                </span>
              </p>
            )}
            {success.invoiceUrl && (
              <a
                href={success.invoiceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="view-invoice-btn"
              >
                <Eye size={18} />
                <span>צפה בחשבונית</span>
              </a>
            )}
          </div>
        ) : (
          <>
            {/* Body */}
            <div className="invoice-quick-modal-body">
              {/* Patient Picker - Only show if not in pre-filled mode */}
              {!isPrefilledMode ? (
                <div className="form-group patient-picker">
                  <label>
                    <User size={16} />
                    בחר מטופל
                  </label>
                  <div className="patient-dropdown-container">
                    <button
                      type="button"
                      className="patient-dropdown-trigger"
                      onClick={() =>
                        setShowPatientDropdown(!showPatientDropdown)
                      }
                      disabled={isCreating}
                    >
                      <span>
                        {selectedPatientId
                          ? patients.find((p) => p.id === selectedPatientId)
                            ? `${
                                patients.find(
                                  (p) => p.id === selectedPatientId
                                ).firstName
                              } ${
                                patients.find(
                                  (p) => p.id === selectedPatientId
                                ).lastName
                              }`
                            : patientName
                          : "בחר מטופל מהרשימה..."}
                      </span>
                      <CaretDown
                        size={16}
                        className={showPatientDropdown ? "rotated" : ""}
                      />
                    </button>

                    {showPatientDropdown && (
                      <div className="patient-dropdown">
                        <input
                          type="text"
                          className="patient-search"
                          placeholder="חפש מטופל..."
                          value={patientSearchQuery}
                          onChange={(e) =>
                            setPatientSearchQuery(e.target.value)
                          }
                          autoFocus
                        />
                        <div className="patient-list">
                          {filteredPatients.length === 0 ? (
                            <div className="no-patients">לא נמצאו מטופלים</div>
                          ) : (
                            filteredPatients.map((patient) => (
                              <button
                                key={patient.id}
                                className={`patient-option ${
                                  selectedPatientId === patient.id
                                    ? "selected"
                                    : ""
                                }`}
                                onClick={() => handleSelectPatient(patient)}
                              >
                                <User size={16} />
                                <span>
                                  {patient.firstName} {patient.lastName}
                                </span>
                                {selectedPatientId === patient.id && (
                                  <Check size={16} className="check-icon" />
                                )}
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                // Pre-filled mode - just show summary
                <div className="invoice-summary">
                  <div className="summary-row">
                    <User size={18} />
                    <span className="label">לקוח:</span>
                    <span className="value">{patientName}</span>
                  </div>
                </div>
              )}

              {/* Patient Appointments - Show when patient is selected (and not pre-filled mode) */}
              {selectedPatientId && !isPrefilledMode && (
                <div className="patient-appointments-section">
                  <div className="section-header">
                    <CalendarBlank size={16} />
                    <span>פגישות (בחר לשיוך חשבונית - ניתן לבחור מספר פגישות)</span>
                    <button
                      className="refresh-btn"
                      onClick={() =>
                        loadPatientAppointments(selectedPatientId, 1, false)
                      }
                      disabled={appointmentsLoading}
                    >
                      <ArrowsClockwise
                        size={14}
                        className={appointmentsLoading ? "spinning" : ""}
                      />
                    </button>
                  </div>

                  <div className="appointments-list">
                    {appointmentsLoading && patientAppointments.length === 0 ? (
                      <ContentLoader size="sm" compact message="טוען פגישות..." />
                    ) : patientAppointments.length === 0 ? (
                      <div className="no-appointments">
                        אין פגישות שהושלמו למטופל זה
                      </div>
                    ) : (
                      <>
                        {patientAppointments.map((apt) => (
                          <button
                            key={apt.id}
                            className={`appointment-item ${
                              isAppointmentSelected(apt) ? "selected" : ""
                            } ${hasInvoice(apt) ? "has-invoice" : ""}`}
                            onClick={() => handleSelectAppointment(apt)}
                            disabled={isCreating}
                          >
                            <div className="apt-main">
                              <div className="apt-date">
                                <CalendarBlank size={14} />
                                <span>{formatDate(apt.startTime)}</span>
                                <span className="apt-time">
                                  {formatTime(apt.startTime)}
                                </span>
                              </div>
                              {(apt.paymentAmount || apt.actualPaidAmount) && (
                                <div className="apt-amount">
                                  {formatCurrency(
                                    apt.actualPaidAmount || apt.paymentAmount
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="apt-status">
                              {hasInvoice(apt) ? (
                                <span className="invoice-badge has">
                                  <Receipt size={12} />
                                  יש חשבונית
                                </span>
                              ) : (
                                <span className="invoice-badge none">
                                  ללא חשבונית
                                </span>
                              )}
                              {isAppointmentSelected(apt) && (
                                <Check
                                  size={16}
                                  weight="bold"
                                  className="selected-check"
                                />
                              )}
                            </div>
                          </button>
                        ))}

                        {hasMoreAppointments && (
                          <button
                            className="load-more-btn"
                            onClick={handleLoadMoreAppointments}
                            disabled={appointmentsLoading}
                          >
                            {appointmentsLoading ? (
                              <>
                                <LoaderLoop size="xs" />
                                <span>טוען...</span>
                              </>
                            ) : (
                              <span>טען 10 נוספות</span>
                            )}
                          </button>
                        )}
                      </>
                    )}
                  </div>

                  {selectedAppointments.length > 0 && (
                    <div className="selected-appointment-info">
                      <LinkSimple size={14} />
                      <span>
                        {selectedAppointments.length === 1
                          ? `החשבונית תשויך לפגישה מתאריך ${formatDate(
                              selectedAppointments[0].startTime
                            )}`
                          : `החשבונית תשויך ל-${selectedAppointments.length} פגישות`}
                      </span>
                      {selectedAppointments.length > 1 && (
                        <span className="total-hint">
                          (סה״כ: {formatCurrency(calculatedTotalFromAppointments)})
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Invoice Form */}
              <div className="invoice-form">
                {/* Manual client name input - only if no patient selected */}
                {!selectedPatientId && !isPrefilledMode && (
                  <div className="form-group full-width">
                    <label>
                      <User size={16} />
                      או הזן שם לקוח ידנית
                    </label>
                    <input
                      type="text"
                      value={patientName}
                      onChange={(e) => setPatientName(e.target.value)}
                      placeholder="שם מלא"
                      disabled={isCreating}
                    />
                  </div>
                )}

                <div className="form-row">
                  <div className="form-group">
                    <label className="required">
                      <CurrencyCircleDollar size={16} />
                      סכום (₪)
                    </label>
                    <input
                      type="number"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      disabled={isCreating}
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      <FileText size={16} />
                      סוג מסמך
                    </label>
                    <select
                      value={documentType}
                      onChange={(e) => setDocumentType(Number(e.target.value))}
                      disabled={isCreating}
                    >
                      {documentTypeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {MORNING_DOCUMENT_HINTS[documentType] && (
                      <span className="iqm-field-hint">
                        {MORNING_DOCUMENT_HINTS[documentType]}
                      </span>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>תיאור השירות</label>
                    <input
                      type="text"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="טיפול"
                      disabled={isCreating}
                    />
                  </div>
                  {documentNeeds.payment && (
                    <div className="form-group">
                      <label>
                        <CurrencyCircleDollar size={16} />
                        אמצעי תשלום
                      </label>
                      <select
                        value={paymentType}
                        onChange={(e) => setPaymentType(Number(e.target.value))}
                        disabled={isCreating}
                      >
                        {getPaymentTypeOptions().map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>

              {/* Error */}
              {error && (
                <div className="error-message">
                  <WarningCircle size={18} weight="fill" />
                  <span>{error}</span>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="invoice-quick-modal-footer">
              <button
                className="skip-btn"
                onClick={handleSkip}
                disabled={isCreating}
              >
                {isPrefilledMode ? "דלג" : "ביטול"}
              </button>
              <button
                className="create-btn"
                onClick={handleCreateInvoice}
                disabled={isCreating}
              >
                {isCreating ? (
                  <>
                    <LoaderLoop size="xs" />
                    <span>מפיק חשבונית...</span>
                  </>
                ) : (
                  <>
                    <Receipt size={18} />
                    <span>הפק חשבונית</span>
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default InvoiceQuickModal;
